import axios from "axios";
import config from "@/lib/config";
import { attachApiHeaders } from "@/lib/attachApiHeaders";

const API_URL = `${config.apiUrl}/bank-request`;

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use(attachApiHeaders);

/**
 * Envía una solicitud de bank request con archivo y tipo de compra.
 * Llama a: POST /bank-request
 * @param {File} file - Archivo a subir
 * @param {string} purchaseType - Tipo de compra (basic|premium; slot legacy)
 * @param {object} [opts]
 * @param {'plan'|'profile'|'slot'|'renewal'} [opts.productType]
 * @param {string} [opts.profileId]
 * @returns {Promise<Object>}
 */
export const createBankRequest = async (file, purchaseType, opts = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("purchaseType", purchaseType);
  const productType =
    opts.productType || (purchaseType === "slot" ? "slot" : "plan");
  formData.append("productType", productType);
  if (opts.profileId) {
    formData.append("profileId", opts.profileId);
  }

  try {
    const res = await api.post("/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  } catch (error) {
    console.error(
      "Error creating bank request:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Obtiene las transferencias pendientes del usuario para perfiles nuevos.
 */
export const getMyPendingProfilePurchases = async () => {
  const res = await api.get("/my-pending-profile-purchases");
  return Array.isArray(res.data) ? res.data : [];
};

/**
 * Actualiza el estado de una solicitud bancaria.
 * Llama a: POST /bank-request/status
 * @param {string} requestId - ID de la solicitud
 * @param {'approved'|'rejected'} status - Nuevo estado
 * @returns {Promise<Object>}
 */
export const updateBankRequestStatus = async (requestId, status) => {
  try {
    const res = await api.post("/status", { requestId, status });
    return res.data;
  } catch (error) {
    console.error(
      "Error updating bank request status:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Obtiene una lista paginada de solicitudes bancarias.
 * Llama a: POST /bank-request/list
 * @param {'approved'|'rejected'|'pending'} status - Estado de las solicitudes
 * @param {number} [page] - Página (opcional)
 * @param {number} [limit] - Límite de resultados por página (opcional)
 * @returns {Promise<Object>}
 */
export const getPaginatedBankRequests = async (status, page, limit) => {
  try {
    const res = await api.post("/list", { status, page, limit });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching paginated bank requests:",
      error.response?.data || error.message
    );
    throw error;
  }
};